(function () {
  "use strict";

  var SHOP = "5135b9-3.myshopify.com";
  var BG_RE = /^\s*bg(?:\s*-\s*|\s+)?(\d{6,20})\s*$/i;
  var ID_QUERY_RE = /^id:(\d{6,20})$/;
  var CANONICAL_RE = /^BG-(\d{6,20})$/;
  var DEBOUNCE_MS = 180;
  var SUBMIT_TIMEOUT_MS = 2200;

  function parseBg(value) {
    var match = BG_RE.exec(String(value == null ? "" : value));
    return match ? match[1] : null;
  }

  function parseTransformed(query, marker) {
    var queryMatch = ID_QUERY_RE.exec(String(query == null ? "" : query));
    var markerMatch = CANONICAL_RE.exec(String(marker == null ? "" : marker));
    return queryMatch && markerMatch && queryMatch[1] === markerMatch[1]
      ? queryMatch[1]
      : null;
  }

  function rootPath(win) {
    var root = win.Shopify && win.Shopify.routes && win.Shopify.routes.root;
    if (typeof root !== "string" || root.charAt(0) !== "/") return "/";
    return root.replace(/\/?$/, "/");
  }

  function sameOriginUrl(win, path) {
    var url = new win.URL(path, win.location.origin);
    if (url.origin !== win.location.origin) throw new Error("Cross-origin URL rejected");
    return url;
  }

  function productResults(payload) {
    var products = payload && payload.resources && payload.resources.results &&
      payload.resources.results.products;
    return Array.isArray(products) ? products : [];
  }

  function decimalId(value) {
    if (typeof value === "number" && Number.isSafeInteger(value)) return String(value);
    return typeof value === "string" && /^\d+$/.test(value) ? value : null;
  }

  function safeHandle(value) {
    return typeof value === "string" && /^[a-z0-9][a-z0-9-]*$/i.test(value)
      ? value : null;
  }

  function dedupeProducts(products) {
    var seen = Object.create(null);
    return products.filter(function (product) {
      var key = decimalId(product && product.id) ||
        (product && typeof product.handle === "string" ? "h:" + product.handle : null);
      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function fetchJson(win, url, signal) {
    return win.fetch(url.href, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: signal
    }).then(function (response) {
      if (!response.ok) {
        var error = new Error("Shopify request failed (" + response.status + ")");
        error.status = response.status;
        throw error;
      }
      return response.json();
    });
  }

  /*
   * A resolution deliberately has three states:
   * alias       - a public product with the exact id and a blank SKU exists
   * no-match    - Shopify answered successfully but it is not a BG alias
   * unavailable - a network/public-product request failed, so we must not claim no match
   */
  function resolveBg(win, rawQuery, options) {
    var id = parseBg(rawQuery);
    if (!id) return Promise.resolve({ state: "not-bg", query: String(rawQuery || "") });

    var root = rootPath(win);
    var signal = options && options.signal;
    var normalUrl = sameOriginUrl(win, root + "search/suggest.json");
    normalUrl.searchParams.set("q", String(rawQuery).trim());
    normalUrl.searchParams.set("resources[type]", "product");
    normalUrl.searchParams.set("resources[limit]", "10");
    var idUrl = sameOriginUrl(win, root + "search/suggest.json");
    idUrl.searchParams.set("q", "id:" + id);
    idUrl.searchParams.set("resources[type]", "product");
    idUrl.searchParams.set("resources[limit]", "10");

    function settled(promise) {
      return promise.then(function (value) {
        return { ok: true, value: value };
      }, function (error) {
        if (error && error.name === "AbortError") throw error;
        return { ok: false, error: error };
      });
    }

    return Promise.all([
      settled(fetchJson(win, normalUrl, signal)),
      settled(fetchJson(win, idUrl, signal))
    ]).then(function (responses) {
      if (!responses[0].ok) {
        return { state: "unavailable", id: id, error: responses[0].error };
      }
      var normal = productResults(responses[0].value);
      if (!responses[1].ok) {
        return normal.length > 0
          ? { state: "native", id: id, products: dedupeProducts(normal) }
          : { state: "unavailable", id: id, error: responses[1].error };
      }
      var byId = productResults(responses[1].value);
      var nativeProducts = dedupeProducts(normal);
      var candidate = byId.find(function (product) {
        return decimalId(product && product.id) === id && safeHandle(product && product.handle);
      });
      if (!candidate) {
        return {
          state: normal.length > 0 ? "native" : "no-match",
          id: id,
          products: nativeProducts
        };
      }

      var productUrl = sameOriginUrl(win, root + "products/" + safeHandle(candidate.handle) + ".js");
      /* Cache prevention is intentional: alias eligibility must reflect the public product now. */
      productUrl.searchParams.set("_bg", String(Date.now()));
      return fetchJson(win, productUrl, signal).then(function (fresh) {
        if (decimalId(fresh && fresh.id) !== id ||
            safeHandle(fresh && fresh.handle) !== safeHandle(candidate.handle)) {
          return {
            state: normal.length > 0 ? "native" : "no-match",
            id: id,
            products: nativeProducts
          };
        }
        var variants = Array.isArray(fresh.variants) ? fresh.variants : [];
        var blankVariant = variants.find(function (variant) {
          return variant && (
            variant.sku == null ||
            (typeof variant.sku === "string" && variant.sku.trim() === "")
          );
        });
        if (!blankVariant) {
          return {
            state: normal.length > 0 ? "native" : "no-match",
            id: id,
            products: nativeProducts
          };
        }
        var aliasProduct = Object.assign({}, candidate, {
          id: fresh.id,
          handle: fresh.handle,
          title: fresh.title || candidate.title,
          url: root + "products/" + fresh.handle + "?variant=" +
            encodeURIComponent(String(blankVariant.id))
        });
        var merged = dedupeProducts(nativeProducts.map(function (product) {
          return decimalId(product && product.id) === id ? aliasProduct : product;
        }).concat([aliasProduct]));
        if (normal.length > 0) {
          return {
            state: "native",
            id: id,
            product: aliasProduct,
            products: merged
          };
        }
        return {
          state: "alias",
          id: id,
          canonical: "BG-" + id,
          effectiveQuery: "id:" + id,
          product: aliasProduct,
          products: merged
        };
      }, function (error) {
        if (error && error.name === "AbortError") throw error;
        if (normal.length > 0) {
          return { state: "native", id: id, products: nativeProducts };
        }
        return { state: "unavailable", id: id, error: error };
      });
    }).catch(function (error) {
      if (error && error.name === "AbortError") throw error;
      return { state: "unavailable", id: id, error: error };
    });
  }

  function withTimeout(win, promise, milliseconds) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = win.setTimeout(function () {
        if (!settled) {
          settled = true;
          resolve({ state: "unavailable", error: new Error("Timed out") });
        }
      }, milliseconds);
      promise.then(function (value) {
        if (!settled) {
          settled = true;
          win.clearTimeout(timer);
          resolve(value);
        }
      }, function (error) {
        if (!settled) {
          settled = true;
          win.clearTimeout(timer);
          resolve({ state: "unavailable", error: error });
        }
      });
    });
  }

  function searchUrlForForm(win, form, query) {
    var url = sameOriginUrl(win, form.getAttribute("action") || rootPath(win) + "search");
    var controls = form.querySelectorAll("input[name],select[name],textarea[name]");
    Array.prototype.forEach.call(controls, function (control) {
      if (control.disabled || !control.name ||
          ((control.type === "checkbox" || control.type === "radio") && !control.checked)) return;
      url.searchParams.append(control.name, control.value);
    });
    url.searchParams.set("q", query);
    return url;
  }

  function transformedSearchUrl(win, query, canonical, existingUrl) {
    var url = existingUrl || sameOriginUrl(win, rootPath(win) + "search");
    url.searchParams.set("q", query);
    url.searchParams.set("so_bg", canonical);
    if (!url.searchParams.has("sort_by")) url.searchParams.set("sort_by", "price-descending");
    return url;
  }

  function isSearchInput(win, input) {
    if (!input || input.name !== "q" || !input.form ||
        typeof input.form.getAttribute !== "function") return false;
    var action = input.form.getAttribute("action");
    if (!action) return false;
    try {
      return /\/search\/?$/.test(sameOriginUrl(win, action).pathname);
    } catch (_) {
      return false;
    }
  }

  function nativeSearchRoot(input) {
    return input && typeof input.closest === "function"
      ? input.closest("[data-live-search]")
      : null;
  }

  function suppressNative(input) {
    var root = nativeSearchRoot(input);
    if (root) root.classList.add("bg-search-native-suppressed");
  }

  function restoreNative(input) {
    var root = nativeSearchRoot(input);
    if (root) root.classList.remove("bg-search-native-suppressed");
  }

  function ensureStyles(doc) {
    if (doc.getElementById("bg-search-styles")) return;
    var style = doc.createElement("style");
    style.id = "bg-search-styles";
    style.textContent =
      ".bg-search-native-suppressed [data-live-search-flydown]{display:none!important}" +
      ".bg-search-results-host{position:relative}" +
      ".bg-search-results{position:absolute;box-sizing:border-box;left:0;right:0;font-size:16px;" +
      "top:100%;z-index:2147483000;background:#fff;color:#1f1f1f;border:1px solid #d7d7d7;" +
      "border-radius:4px;box-shadow:0 8px 24px rgba(0,0,0,.18);padding:.75rem;" +
      "max-height:min(70vh,32rem);overflow:auto;text-align:left}" +
      ".bg-search-results p{margin:0 0 .5rem}.bg-search-results ul{list-style:none;" +
      "margin:0;padding:0}.bg-search-results li+li{border-top:1px solid #eee}" +
      ".bg-search-results a{display:block;padding:.7rem .4rem;color:#1f1f1f;" +
      "text-decoration:underline;line-height:1.35}" +
      ".bg-search-results a:focus{outline:2px solid currentColor;outline-offset:2px}" +
      "@media(max-width:749px){.bg-search-results{left:.25rem;right:.25rem;" +
      "max-height:60vh;padding:.6rem}}";
    (doc.head || doc.documentElement).appendChild(style);
  }

  function friendlyInputs(win, doc, id) {
    Array.prototype.forEach.call(doc.querySelectorAll('input[name="q"]'), function (input) {
      if (isSearchInput(win, input) && input.value === "id:" + id) {
        input.value = "BG-" + id;
        suppressNative(input);
      }
    });
  }

  function createPredictiveBox(doc, input) {
    var box = doc.createElement("div");
    box.className = "bg-search-results";
    box.setAttribute("role", "region");
    box.setAttribute("aria-live", "polite");
    var form = input.form;
    var host = form && form.parentNode ? form.parentNode : input.parentNode;
    host.classList.add("bg-search-results-host");
    host.insertBefore(box, (form || input).nextSibling);
    input.setAttribute("aria-controls", box.id || (box.id = "bg-search-" + Math.random().toString(36).slice(2)));
    return box;
  }

  function clearBox(state) {
    if (state.timer) clearTimeout(state.timer);
    if (state.controller) state.controller.abort();
    state.token += 1;
    if (state.box && state.box.parentNode) state.box.parentNode.removeChild(state.box);
    state.box = null;
  }

  function renderPredictive(doc, input, state, result) {
    if (!state.box) state.box = createPredictiveBox(doc, input);
    var box = state.box;
    while (box.firstChild) box.removeChild(box.firstChild);
    var status = doc.createElement("p");
    status.setAttribute("role", "status");
    if (result.state === "unavailable") {
      status.textContent = "Search suggestions are temporarily unavailable.";
      box.appendChild(status);
      return;
    }
    if (result.state !== "alias" && result.state !== "native") {
      status.textContent = "No matching BG product was found.";
      box.appendChild(status);
      return;
    }
    status.textContent = result.products.length + " product result" +
      (result.products.length === 1 ? "" : "s");
    box.appendChild(status);
    var list = doc.createElement("ul");
    result.products.forEach(function (product) {
      var handle = safeHandle(product && product.handle);
      if (!handle) return;
      var item = doc.createElement("li");
      var link = doc.createElement("a");
      var proposed = product.url || (rootPath(window) + "products/" + handle);
      var safeUrl;
      try {
        safeUrl = sameOriginUrl(window, proposed);
      } catch (_) {
        return;
      }
      link.href = safeUrl.pathname + safeUrl.search;
      link.textContent = String(product.title || "Product");
      item.appendChild(link);
      list.appendChild(item);
    });
    box.appendChild(list);
  }

  function install(win, doc) {
    if (!win.Shopify || win.Shopify.shop !== SHOP) return false;
    ensureStyles(doc);
    var states = new WeakMap();
    var submitTokens = new WeakMap();

    doc.addEventListener("input", function (event) {
      var input = event.target;
      if (!isSearchInput(win, input)) return;
      if (input.form) {
        submitTokens.set(input.form, (submitTokens.get(input.form) || 0) + 1);
      }
      var id = parseBg(input.value);
      var state = states.get(input);
      if (!id) {
        restoreNative(input);
        if (state) clearBox(state);
        return;
      }
      event.stopImmediatePropagation();
      suppressNative(input);
      if (!state) {
        state = { token: 0, timer: null, controller: null, box: null };
        states.set(input, state);
      }
      clearBox(state);
      var token = state.token;
      state.timer = win.setTimeout(function () {
        state.controller = typeof win.AbortController === "function" ? new win.AbortController() : null;
        withTimeout(win, resolveBg(win, input.value, {
          signal: state.controller && state.controller.signal
        }), SUBMIT_TIMEOUT_MS).then(function (result) {
          if (token !== state.token || parseBg(input.value) !== id) return;
          renderPredictive(doc, input, state, result);
        }).catch(function (error) {
          if (error && error.name !== "AbortError" && token === state.token) {
            renderPredictive(doc, input, state, { state: "unavailable" });
          }
        });
      }, DEBOUNCE_MS);
    }, true);

    doc.addEventListener("keyup", function (event) {
      var input = event.target;
      if (isSearchInput(win, input) && parseBg(input.value)) {
        event.stopImmediatePropagation();
        suppressNative(input);
      }
    }, true);

    doc.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      if (!isSearchInput(win, event.target)) return;
      restoreNative(event.target);
      var state = states.get(event.target);
      if (state) clearBox(state);
    }, true);

    doc.addEventListener("submit", function (event) {
      var form = event.target;
      if (!form || !form.querySelector) return;
      var input = form.querySelector('input[name="q"]');
      if (!isSearchInput(win, input)) return;
      var id = input && parseBg(input.value);
      if (!id) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var submittedValue = input.value;
      var submitToken = (submitTokens.get(form) || 0) + 1;
      submitTokens.set(form, submitToken);
      var originalUrl;
      try {
        originalUrl = searchUrlForForm(win, form, input.value);
      } catch (_) {
        originalUrl = sameOriginUrl(win, rootPath(win) + "search");
        originalUrl.searchParams.set("q", input.value);
      }
      withTimeout(win, resolveBg(win, input.value), SUBMIT_TIMEOUT_MS).then(function (result) {
        if (submitTokens.get(form) !== submitToken || input.value !== submittedValue) return;
        var destination = result.state === "alias"
          ? transformedSearchUrl(win, result.effectiveQuery, result.canonical, originalUrl)
          : originalUrl;
        win.location.assign(destination.href);
      });
    }, true);

    function onReady() {
      var current = new win.URL(win.location.href);
      var searchPath = rootPath(win) + "search";
      if (current.pathname !== searchPath.replace(/\/{2,}/g, "/")) return;
      var transformedId = parseTransformed(
        current.searchParams.get("q"),
        current.searchParams.get("so_bg")
      );
      if (transformedId) {
        friendlyInputs(win, doc, transformedId);
        return;
      }
      var id = parseBg(current.searchParams.get("q"));
      if (!id) return;
      withTimeout(
        win,
        resolveBg(win, current.searchParams.get("q")),
        SUBMIT_TIMEOUT_MS
      ).then(function (result) {
        if (result.state !== "alias") return;
        var destination = transformedSearchUrl(
          win,
          result.effectiveQuery,
          result.canonical,
          current
        );
        win.location.replace(destination.href);
      });
    }
    if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", onReady);
    else onReady();
    return true;
  }

  if (window.__BG_SEARCH_TEST__ === true) {
    window.__BG_SEARCH_TEST_API__ = {
      parseBg: parseBg,
      parseTransformed: parseTransformed,
      resolveBg: resolveBg,
      dedupeProducts: dedupeProducts,
      transformedSearchUrl: transformedSearchUrl,
      install: install
    };
  }
  install(window, document);
}());